import { useContext, useMemo } from 'react';
import { groupBy, sortBy } from 'lodash';
import { nanoid } from 'nanoid';
import innerText from 'react-innertext';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import SidePanel, { PanelMainGroup, PanelSection } from '../SidePanel';
import {
  HiddenButton,
  HoverNavItem,
  IconSpan,
  NavMainItem,
  StickyHeader,
  StyledNameDiv,
  WrappedNameInput,
} from '../left-menu/menu-components';
import { useMultiProjectDashboardContext } from '../../contexts/MultiProjectDashboardContext';
import { ItemContext } from '../../contexts/ItemContext';
import { farmOwnerText } from '../../utils/columns';
import { asRelativeTime } from '../../utils/formatters';
import MinusIcon from '../icons/MinusIcon';
import PinIcon from '../icons/PinIcon';
import { useUser } from '../../utils/hooks';
import { NEW_DASHBOARD_ID } from '../../hooks/useDashboardState';
import { captureEvent } from '../../utils/analytics';
import DashboardViewsDropdown from './DashboardViewsDropdown';
import InlineButton from '../basics/InlineButton';

// This container is used to provide a larger click target for the minus icon
// without changing its size or appearance.
const HoverContainer = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  top: -0.3rem;
  &:hover svg {
    color: var(--text-color);
  }
  svg {
    position: relative;
    top: 0.3rem;
    left: 25%;
    color: var(--text-muted-color);
  }
`;

const FarmItem = ({ id, name, tags, updatedAt, owner, color }) => {
  const user = useUser();
  const { setActiveKey, clearActiveKey, activeKey } = useContext(ItemContext);
  const {
    dashboards: { removeDashboardFarm, toggleFarm, selectedFarmId },
  } = useMultiProjectDashboardContext();

  const hoverInfo = useMemo(
    () =>
      [
        ['Name', name],
        ['Tags', tags.join(', ')],
        ['Last Modified', innerText(asRelativeTime(updatedAt))],
        ['Owner', farmOwnerText(owner, user)],
      ]
        .filter(([, v]) => v)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n'),
    [name, owner, tags, updatedAt, user],
  );

  return (
    <HoverNavItem
      onClick={() => toggleFarm(id)}
      onMouseEnter={() => setActiveKey(id)}
      onMouseLeave={clearActiveKey}
      hovered={activeKey === id}
      isActive={selectedFarmId === id}
    >
      <IconSpan width="18px" className="ml-2">
        <PinIcon color={color} />
      </IconSpan>
      <StyledNameDiv title={hoverInfo}>{name}</StyledNameDiv>
      <HiddenButton
        className="ml-2"
        onClick={(e) => {
          e.stopPropagation();
          captureEvent('dashboard-farm-removed', { location: 'left-menu' });
          removeDashboardFarm(id);
        }}
      >
        <HoverContainer>
          <MinusIcon />
        </HoverContainer>
      </HiddenButton>
    </HoverNavItem>
  );
};

const DashboardLeftMenu = () => {
  const {
    dashboards: {
      dashboards,
      dashboardFarms,
      clearDashboardFarms,
      setDashboardName,
      dashboardLoading,
      activeDashboard,
      activeDashboardId,
    },
  } = useMultiProjectDashboardContext();
  const navigate = useNavigate();

  const groupedTeamFarms = useMemo(() => {
    const groups = groupBy(dashboardFarms, (f) => f.team?.id);
    return sortBy(Object.values(groups), (teamFarms) =>
      teamFarms[0].team?.name?.toLowerCase(),
    );
  }, [dashboardFarms]);

  return (
    <SidePanel>
      <PanelMainGroup>
        {dashboards.length > 1 && (
          <PanelSection>
            <DashboardViewsDropdown dashboards={dashboards} />
          </PanelSection>
        )}
        <PanelSection>
          <StickyHeader className="justify-content-between align-items-start">
            {activeDashboardId === NEW_DASHBOARD_ID ? (
              <>
                <span>Selected Projects</span>
                <InlineButton
                  color="link"
                  className="p-0 font-small text-muted"
                  onClick={clearDashboardFarms}
                >
                  Clear All
                </InlineButton>
              </>
            ) : (
              <>
                <WrappedNameInput
                  name={activeDashboard?.name}
                  update={setDashboardName}
                />
                <InlineButton
                  color="link"
                  className="p-0 font-small text-muted flex-shrink-0"
                  onClick={() => navigate('/app/dashboard')}
                >
                  Exit View
                </InlineButton>
              </>
            )}
          </StickyHeader>
          {dashboardLoading ? (
            <div>Loading...</div>
          ) : (
            <ul>
              {groupedTeamFarms.map((teamFarms) => {
                const { team } = teamFarms[0];
                return (
                  <div className="mb-2" key={team?.id || nanoid()}>
                    <NavMainItem hoverable={false}>
                      {team?.name || <i>None</i>}
                    </NavMainItem>
                    {sortBy(teamFarms, (f) => f.name.toLowerCase()).map(
                      (farm) => (
                        <FarmItem key={farm.id} {...farm} />
                      ),
                    )}
                  </div>
                );
              })}
            </ul>
          )}
        </PanelSection>
      </PanelMainGroup>
    </SidePanel>
  );
};
export default DashboardLeftMenu;
